import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import exploration2 from "../assets/images/2-personal exp.jpg";
import workCard3 from "../assets/images/work-card-3-new.png";
import contactIcon from "../assets/images/contact-icon.png";
import aboutVideo from "../assets/video.mp4";
import styles from "./Home.module.css";

const Home: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const backgrounds = [
    "/images/New%20Main%20Pics/1%281%29.png",
    "/images/New%20Main%20Pics/2.jpg",
    "/images/New%20Main%20Pics/3%281%29.png",
    "/images/New%20Main%20Pics/4.png",
    "/images/New%20Main%20Pics/5.png",
    "/images/New%20Main%20Pics/6.png",
  ];

  const healthyLifeImages = [
    "/images/health%20Life/1-HL.png",
    "/images/health%20Life/2-HL.JPG",
    "/images/health%20Life/3-HL.JPG",
    "/images/health%20Life/4-HL.JPG",
    "/images/health%20Life/5-HL.JPG",
    "/images/health%20Life/6-HL.jpg",
    "/images/health%20Life/7-HL.jpg",
    "/images/health%20Life/8-HL.HEIC",
    "/images/health%20Life/9-HL.jpg",
    "/images/health%20Life/10-HL.JPG",
    "/images/health%20Life/11-HL.PNG",
    "/images/health%20Life/12-HL.JPG",
    "/images/health%20Life/13-HL.JPG",
    "/images/health%20Life/14-HL.HEIC",
    "/images/health%20Life/15-HL.PNG",
    "/images/health%20Life/16-HL.JPG",
    "/images/health%20Life/17-HL.JPG",
    "/images/health%20Life/18-HL.JPG",
    "/images/health%20Life/19-HL.png",
    "/images/health%20Life/20-HL.heic",
    "/images/health%20Life/21-HL.JPG",
    "/images/health%20Life/22-HL.JPG",
    "/images/health%20Life/23-HL.JPG",
    "/images/health%20Life/24-HL.PNG",
    "/images/health%20Life/25-HL.jpg",
    "/images/health%20Life/26-HL.JPG",
    "/images/health%20Life/27-HL.png",
    "/images/health%20Life/28-HL%20%282%29.png",
  ];

  const imagesPerPage = 15;
  const totalPages = Math.ceil(healthyLifeImages.length / imagesPerPage);

  // Create infinite loop: [lastClone, img1, img2, firstClone]
  const slides = [
    backgrounds[backgrounds.length - 1],
    ...backgrounds,
    backgrounds[0],
  ];

  const [currentIndex, setCurrentIndex] = useState(1);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const carouselRef = useRef<HTMLDivElement>(null);
  const autoPlayRef = useRef<NodeJS.Timeout | null>(null);

  const [healthyLifePage, setHealthyLifePage] = useState(0);
  const [healthyLifeTouchStart, setHealthyLifeTouchStart] = useState(0);
  const [healthyLifeTouchEnd, setHealthyLifeTouchEnd] = useState(0);
  const [isHealthyLifeDragging, setIsHealthyLifeDragging] = useState(false);

  // Handle transition end for infinite loop reset
  const handleTransitionEnd = () => {
    setIsTransitioning(false);

    // If at clone of last image (index 0), jump to real last image
    if (currentIndex === 0) {
      setIsTransitioning(false);
      setCurrentIndex(backgrounds.length);
    }
    // If at clone of first image (index slides.length - 1), jump to real first image
    else if (currentIndex === slides.length - 1) {
      setIsTransitioning(false);
      setCurrentIndex(1);
    }
  };

  const goToNext = () => {
    setIsTransitioning(true);
    setCurrentIndex((prev) => prev + 1);
  };

  const goToPrev = () => {
    setIsTransitioning(true);
    setCurrentIndex((prev) => prev - 1);
  };

  // Auto-play
  useEffect(() => {
    autoPlayRef.current = setInterval(goToNext, 5000);
    return () => {
      if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    };
  }, []);

  // Reset auto-play on manual interaction
  const resetAutoPlay = () => {
    if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    autoPlayRef.current = setInterval(goToNext, 5000);
  };

  const handleTouchStart = (e: React.TouchEvent | React.MouseEvent) => {
    setIsDragging(true);
    const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
    setTouchStart(clientX);
  };

  const handleTouchMove = (e: React.TouchEvent | React.MouseEvent) => {
    if (!isDragging) return;
    const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
    setTouchEnd(clientX);
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);

    const swipeThreshold = 50;
    const diff = touchStart - touchEnd;

    if (diff > swipeThreshold) {
      // Swiped left, go to next
      goToNext();
      resetAutoPlay();
    } else if (diff < -swipeThreshold) {
      // Swiped right, go to previous
      goToPrev();
      resetAutoPlay();
    }

    setTouchStart(0);
    setTouchEnd(0);
  };

  const handleHealthyLifeTouchStart = (e: React.TouchEvent | React.MouseEvent) => {
    setIsHealthyLifeDragging(true);
    const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
    setHealthyLifeTouchStart(clientX);
  };

  const handleHealthyLifeTouchMove = (e: React.TouchEvent | React.MouseEvent) => {
    if (!isHealthyLifeDragging) return;
    const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
    setHealthyLifeTouchEnd(clientX);
  };

  const handleHealthyLifeTouchEnd = () => {
    if (!isHealthyLifeDragging) return;
    setIsHealthyLifeDragging(false);

    const swipeThreshold = 50;
    const diff = healthyLifeTouchStart - healthyLifeTouchEnd;

    if (diff > swipeThreshold && healthyLifePage < totalPages - 1) {
      setHealthyLifePage(healthyLifePage + 1);
    } else if (diff < -swipeThreshold && healthyLifePage > 0) {
      setHealthyLifePage(healthyLifePage - 1);
    }

    setHealthyLifeTouchStart(0);
    setHealthyLifeTouchEnd(0);
  };

  const handleHealthyLifePrevClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (healthyLifePage > 0) {
      setHealthyLifePage(healthyLifePage - 1);
    }
  };

  const handleHealthyLifeNextClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (healthyLifePage < totalPages - 1) {
      setHealthyLifePage(healthyLifePage + 1);
    }
  };

  const visibleImages = healthyLifeImages.slice(
    healthyLifePage * imagesPerPage,
    (healthyLifePage + 1) * imagesPerPage
  );

  const handlePrevClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    goToPrev();
    resetAutoPlay();
  };

  const handleNextClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    goToNext();
    resetAutoPlay();
  };

  return (
    <>
      <div className={styles.heroSection}>
        <div
          className={styles.bgCarousel}
          ref={carouselRef}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleTouchStart}
          onMouseMove={handleTouchMove}
          onMouseUp={handleTouchEnd}
          onMouseLeave={() => {
            if (isDragging) handleTouchEnd();
          }}
        >
          <div
            className={styles.bgSlides}
            style={{
              transform: `translateX(-${currentIndex * 100}%)`,
              transition: isTransitioning
                ? "transform 0.5s ease-in-out"
                : "none",
            }}
            onTransitionEnd={handleTransitionEnd}
          >
            {slides.map((bg, index) => (
              <div
                key={index}
                className={styles.bgSlide}
              >
                <img src={bg} alt={`Slide ${index}`} className={styles.bgImage} />
              </div>
            ))}
          </div>
        </div>
        <button
          className={styles.carouselNavButton + ' ' + styles.carouselNavButtonLeft}
          onClick={handlePrevClick}
          aria-label="Previous slide"
        >
          &#8249;
        </button>
        <button
          className={styles.carouselNavButton + ' ' + styles.carouselNavButtonRight}
          onClick={handleNextClick}
          aria-label="Next slide"
        >
          &#8250;
        </button>
        <div className={styles.overlay} />
        <Header />
        <main className={styles.homeContent}>
          <div className={styles.contentContainer}>
            <h1 className={styles.mainHeading}>{t("home.title")}</h1>
            <p className={styles.description}>{t("home.description")}</p>
          </div>
        </main>
      </div>

      <section className={styles.aboutSection}>
        <div className={styles.aboutCard}>
          <div className={styles.aboutImageContainer}>
            <video
              src={aboutVideo}
              className={styles.aboutImage}
              controls
              playsInline
            />
          </div>
          <div className={styles.aboutContent}>
            <h2 className={styles.aboutTitle}>{t("home.aboutTitle")}</h2>
            <p className={styles.aboutText}>{t("home.aboutText")}</p>
            <p className={styles.aboutResumeLink}>
              {t("home.aboutResumeText")}{" "}
              <a href={String(t("home.aboutResumeLink"))} target="_blank" rel="noopener noreferrer">
                View Resume
              </a>
            </p>
          </div>
        </div>
      </section>

      <section className={styles.explorationsSection}>
        <div className={styles.explorationsContainer}>
          <h2 className={styles.explorationsTitle}>
            {t("home.explorationsTitle")}
          </h2>
          <div className={styles.explorationsCards}>
            <div className={styles.explorationCard}>
              <img
                src="/images/New%20Personal%20Exp/1-personal%20explorations.png"
                alt="Exploration 1"
                className={styles.explorationImage}
              />
              <p className={styles.explorationText}>
                {t("home.explorations.card1")}
              </p>
            </div>
            <div className={styles.explorationCard}>
              <img
                src={exploration2}
                alt="Exploration 2"
                className={`${styles.explorationImage} ${styles.explorationImage2}`}
              />
              <p className={styles.explorationText}>
                {t("home.explorations.card2")}
              </p>
            </div>
            <div className={styles.explorationCard}>
              <img
                src="/images/New%20Personal%20Exp/3-personal%20exp.jpg"
                alt="Exploration 3"
                className={styles.explorationImage}
              />
              <p className={styles.explorationText}>
                {t("home.explorations.card3")}
              </p>
            </div>
            <div className={styles.explorationCard}>
              <img
                src="/images/New%20Personal%20Exp/4-personal%20exp.png"
                alt="Exploration 4"
                className={styles.explorationImage}
              />
              <p className={styles.explorationText}>
                {t("home.explorations.card4")}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className={styles.discoverWorkSection}>
        <div className={styles.discoverWorkContainer}>
          <h2 className={styles.discoverWorkTitle}>
            {t("home.discoverWorkTitle")}
          </h2>
          <div className={styles.discoverWorkCards}>
            <div className={styles.workCard}>
              <img
                src="/images/1-Discover.png"
                alt="Work Card 1"
                className={styles.workCardImage}
              />
              <div className={styles.workCardContent}>
                <h3 className={styles.workCardTitle}>
                  {t("home.discoverWork.card1.title")}
                </h3>
                <p className={styles.workCardDescription}>
                  {t("home.discoverWork.card1.description")}
                </p>
                <button
                  className={styles.workCardButton}
                  onClick={() => {
                    navigate("/project");
                    setTimeout(() => window.scrollTo(0, 0), 0);
                  }}
                >
                  {t("home.discoverWork.card1.button")}
                </button>
              </div>
            </div>
            <div className={styles.workCard}>
              <img
                src="/images/2-Discover.png"
                alt="Work Card 2"
                className={styles.workCardImage}
              />
              <div className={styles.workCardContent}>
                <h3 className={styles.workCardTitle}>
                  {t("home.discoverWork.card2.title")}
                </h3>
                <p className={styles.workCardDescription}>
                  {t("home.discoverWork.card2.description")}
                </p>
                <button
                  className={styles.workCardButton}
                  onClick={() => {
                    navigate("/achievement");
                    setTimeout(() => window.scrollTo(0, 0), 0);
                  }}
                >
                  {t("home.discoverWork.card2.button")}
                </button>
              </div>
            </div>
            <div className={styles.workCard}>
              <img
                src={workCard3}
                alt="Work Card 3"
                className={styles.workCardImage}
              />
              <div className={styles.workCardContent}>
                <h3 className={styles.workCardTitle}>
                  {t("home.discoverWork.card3.title")}
                </h3>
                <p className={styles.workCardDescription}>
                  {t("home.discoverWork.card3.description")}
                </p>
                <button
                  className={styles.workCardButton}
                  onClick={() => {
                    navigate("/impact");
                    setTimeout(() => window.scrollTo(0, 0), 0);
                  }}
                >
                  {t("home.discoverWork.card3.button")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className={styles.healthyLifeSection}>
        <div className={styles.healthyLifeContainer}>
          <h2 className={styles.healthyLifeTitle}>
            {t("home.healthyLifeTitle")}
          </h2>
          <div className={styles.healthyLifeGalleryWrapper}>
            <button
              className={styles.healthyLifeNavButton + ' ' + styles.healthyLifeNavButtonLeft}
              onClick={handleHealthyLifePrevClick}
              aria-label="Previous page"
              disabled={healthyLifePage === 0}
            >
              &#8249;
            </button>
            <div
              className={styles.healthyLifeGallery}
              onTouchStart={handleHealthyLifeTouchStart}
              onTouchMove={handleHealthyLifeTouchMove}
              onTouchEnd={handleHealthyLifeTouchEnd}
              onMouseDown={handleHealthyLifeTouchStart}
              onMouseMove={handleHealthyLifeTouchMove}
              onMouseUp={handleHealthyLifeTouchEnd}
              onMouseLeave={() => {
                if (isHealthyLifeDragging) handleHealthyLifeTouchEnd();
              }}
            >
              {visibleImages.map((image, index) => (
                <img
                  key={index}
                  src={image}
                  alt={`Healthy Life ${healthyLifePage * imagesPerPage + index + 1}`}
                  className={styles.healthyLifeImage}
                />
              ))}
            </div>
            <button
              className={styles.healthyLifeNavButton + ' ' + styles.healthyLifeNavButtonRight}
              onClick={handleHealthyLifeNextClick}
              aria-label="Next page"
              disabled={healthyLifePage === totalPages - 1}
            >
              &#8250;
            </button>
          </div>
          <div className={styles.healthyLifeDots}>
            {Array.from({ length: totalPages }).map((_, index) => (
              <div
                key={index}
                className={`${styles.healthyLifeDot} ${index === healthyLifePage ? styles.active : ''}`}
                onClick={() => setHealthyLifePage(index)}
              />
            ))}
          </div>
        </div>
      </section>

      <section
        className={styles.contactSection}
        onClick={() => navigate("/contact")}
      >
        <div className={styles.contactContainer}>
          <img src={contactIcon} alt="Contact" className={styles.contactIcon} />
          <button className={styles.contactButton}>
            {t("home.contactButton")}
          </button>
        </div>
      </section>
    </>
  );
};

export default Home;

